import { apiRequest } from '../../shared/api/client';

/**
 * Lot traceability — mirrors Invetrak.Api.Endpoints.LotTraceEndpoints, over tenant script 084.
 *
 *   GET /api/v1/lots/{id}/trace                                  lots:read
 *   GET /api/v1/lots/{id}/trace/movements[?pageSize=&pageNumber=] lots:read
 *
 * Two calls rather than one: the summary, the on-hand and the customer list are small and
 * arrive together, and the movement list pages on its own because a lot counted weekly for two
 * years has hundreds of rows.
 *
 * **Warehouse-scoped.** A warehouse is the 3PL client boundary, so the answer is always "in this
 * warehouse" — which is also the honest answer for a recall run by one site.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export interface LotTraceSummaryDto {
  lotId: string;
  lotNumber: string;
  itemId: string;
  itemNumber: string;
  itemDescription: string | null;
  stockingUomCode: string;
  fifoDateUtc: string;
  expirationDateUtc: string | null;
  supplierLotNumber: string | null;
  isExpired: boolean;
  quantityReceived: number;
  /** Positive. The ledger holds a shipment negative; "shipped -40" reads as a return. */
  quantityShipped: number;
  quantityAdjusted: number;
  quantityOnHand: number;
  firstMovementUtc: string | null;
  lastMovementUtc: string | null;
  movementCount: number;
}

export interface LotOnHandDto {
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  locationId: string;
  locationCode: string;
  locationName: string | null;
  balanceTypeCode: string | null;
  balanceTypeName: string | null;
  licensePlateNumber: string | null;
  quantity: number;
  uomCode: string;
}

export interface LotTraceCustomerDto {
  customerId: string;
  customerNumber: string;
  customerName: string;
  salesOrderId: string;
  orderNumber: string;
  customerPo: string | null;
  pickNumber: string | null;
  shippedAtUtc: string | null;
  /** Null when the attribution is not exact — nothing recorded how much. */
  quantity: number | null;
  /**
   * False means the customer was on a WAVE that handled this lot before the wave's split
   * recorded lots. They may or may not have received it, and the screen says so rather than
   * letting a recall list read as certain throughout.
   */
  isExact: boolean;
}

export interface LotMovementDto {
  inventoryTransactionId: string;
  performedAtUtc: string;
  tranCode: string;
  tranCodeName: string;
  movementType: string | null;
  movementSign: number | null;
  quantityDelta: number;
  uomCode: string;
  warehouseCode: string;
  warehouseName: string;
  locationCode: string;
  balanceTypeCode: string | null;
  licensePlateNumber: string | null;
  /** Receipt, Shipment, Pick, Move or Adjustment. */
  documentType: string;
  documentNumber: string | null;
  /** The supplier on a receipt, the customer on a shipment, nobody on a move. */
  counterparty: string | null;
  poNumber: string | null;
  orderNumber: string | null;
  packingSlipNumber: string | null;
  fromWave: boolean;
  performedBy: string | null;
}

export interface LotTraceDto {
  summary: LotTraceSummaryDto;
  onHand: LotOnHandDto[];
  customers: LotTraceCustomerDto[];
}

export interface LotMovementPageDto {
  rows: LotMovementDto[];
  totalCount: number;
}

export async function getLotTrace(lotId: string, signal?: AbortSignal): Promise<LotTraceDto> {
  const { data } = await apiRequest<LotTraceDto>(
    `/v1/lots/${encodeURIComponent(lotId)}/trace`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function getLotMovements(
  lotId: string,
  page: { pageSize?: number; pageNumber?: number } = {},
  signal?: AbortSignal,
): Promise<LotMovementPageDto> {
  const q = new URLSearchParams();
  if (page.pageSize) q.set('pageSize', String(page.pageSize));
  if (page.pageNumber) q.set('pageNumber', String(page.pageNumber));
  const query = q.toString();

  const { data } = await apiRequest<LotMovementPageDto>(
    `/v1/lots/${encodeURIComponent(lotId)}/trace/movements${query ? `?${query}` : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}
